
import React, { useState, useEffect } from 'react';
import { type Editor } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image,
  Table,
  TableProperties,
  Plus,
  Minus,
  MoreHorizontal,
  Maximize2,
  Minimize2,
  Handshake,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImageInsertDialog } from './ImageInsertDialog';
import { LinkInsertDialog } from './LinkInsertDialog';
import { AffiliateCtaInsertDialog } from './AffiliateCtaInsertDialog';

interface EditorToolbarProps {
  editor: Editor | null;
  onToggleFocusMode?: () => void;
  isFocusMode?: boolean;
}

export const EditorToolbar = ({ editor, onToggleFocusMode, isFocusMode }: EditorToolbarProps) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [affiliateDialogOpen, setAffiliateDialogOpen] = useState(false);
  const [linkInitials, setLinkInitials] = useState({ url: '', newTab: false, nofollow: false });


  const addImage = (imageUrl: string) => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
    }
  };

  const openLinkDialog = () => {
    if (!editor) return;
    const attrs = editor.getAttributes('link') || {};
    setLinkInitials({
      url: attrs.href || '',
      newTab: attrs.target === '_blank',
      nofollow: typeof attrs.rel === 'string' ? attrs.rel.includes('nofollow') : false,
    });
    setLinkDialogOpen(true);
  };

  const insertOrUpdateLink = ({ url, newTab, nofollow }: { url: string; newTab: boolean; nofollow: boolean }) => {
    if (!editor) return;
    const relParts = ['noopener', 'noreferrer'];
    if (nofollow) relParts.push('nofollow');
    const rel = relParts.join(' ');

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url, target: newTab ? '_blank' : undefined, rel })
      .run();
  };

  const removeLink = () => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const setTableAlignment = (mode: 'left' | 'center' | 'right' | 'full') => {
    const base = 'table-auto border-collapse border border-border my-4';
    const cls =
      mode === 'left'
        ? `${base} ml-0 mr-auto`
        : mode === 'right'
        ? `${base} ml-auto mr-0`
        : mode === 'full'
        ? `${base} w-full`
        : `${base} mx-auto`;
    editor.chain().focus().updateAttributes('table', { class: cls }).run();
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setImageDialogOpen(true);
  };

  const handleAction = (action: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  };

  const insertAffiliateCta = (partnerId: string, compact: boolean, context?: string) => {
    if (!editor) return;
    
    const displayType = compact ? 'compact' : 'full';
    
    editor.chain().focus().insertContent({
      type: 'affiliateCta',
      attrs: {
        partnerId,
        displayType,
        context: context || null,
      },
    }).run();
    
    setAffiliateDialogOpen(false);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openLinkDialog();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editor]);
  
  if (!editor) {
    return null;
  }
  
  return (
    <>
      <div className="border-b p-2 flex flex-wrap gap-1 items-center">
        {/* Focus mode toggle */}
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={handleAction(() => onToggleFocusMode?.())}
          aria-label="Toggle focus mode"
          title={isFocusMode ? 'Exit focus mode' : 'Enter focus mode'}
        >
          {isFocusMode ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </Button>

        <Separator orientation="vertical" className="h-6" />

        {/* Text formatting */}
        <Button
          variant={editor.isActive('bold') ? 'secondary' : 'ghost'}
          size="sm"
          type="button"
          onClick={handleAction(() => editor.chain().focus().toggleBold().run())}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('italic') ? 'secondary' : 'ghost'}
          size="sm"
          type="button"
          onClick={handleAction(() => editor.chain().focus().toggleItalic().run())}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('strike') ? 'secondary' : 'ghost'}
          size="sm"
          type="button"
          onClick={handleAction(() => editor.chain().focus().toggleStrike().run())}
        >
          <Strikethrough className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        {/* Text alignment */}
        <Button
          variant={editor.isActive({ textAlign: 'left' }) ? 'secondary' : 'ghost'}
          size="sm"
          type="button"
          onClick={handleAction(() => editor.chain().focus().setTextAlign('left').run())}
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive({ textAlign: 'center' }) ? 'secondary' : 'ghost'}
          size="sm"
          type="button"
          onClick={handleAction(() => editor.chain().focus().setTextAlign('center').run())}
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive({ textAlign: 'right' }) ? 'secondary' : 'ghost'}
          size="sm"
          type="button"
          onClick={handleAction(() => editor.chain().focus().setTextAlign('right').run())}
        >
          <AlignRight className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        {/* Lists */}
        <Button
          variant={editor.isActive('bulletList') ? 'secondary' : 'ghost'}
          size="sm"
          type="button"
          onClick={handleAction(() => editor.chain().focus().toggleBulletList().run())}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('orderedList') ? 'secondary' : 'ghost'}
          size="sm"
          type="button"
          onClick={handleAction(() => editor.chain().focus().toggleOrderedList().run())}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          variant={editor.isActive('blockquote') ? 'secondary' : 'ghost'}
          size="sm"
          type="button"
          onClick={handleAction(() => editor.chain().focus().toggleBlockquote().run())}
        >
          <Quote className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        {/* Media */}
        <Button variant="ghost" size="sm" type="button" onClick={handleAction(openLinkDialog)}>
          <LinkIcon className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleImageClick}
          type="button"
        >
          <Image className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={handleAction(() => setAffiliateDialogOpen(true))}
          title="Insert Affiliate CTA"
        >
          <Handshake className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        {/* Table controls */}
        <Button variant="ghost" size="sm" type="button" onClick={handleAction(insertTable)}>
          <Table className="h-4 w-4" />
        </Button>

        {editor.isActive('table') && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" type="button">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() => editor.chain().focus().addColumnBefore().run()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Column Before
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => editor.chain().focus().addColumnAfter().run()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Column After
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => editor.chain().focus().deleteColumn().run()}
                >
                  <Minus className="h-4 w-4 mr-2" />
                  Delete Column
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => editor.chain().focus().addRowBefore().run()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Row Before
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => editor.chain().focus().addRowAfter().run()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Row After
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => editor.chain().focus().deleteRow().run()}
                >
                  <Minus className="h-4 w-4 mr-2" />
                  Delete Row
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => editor.chain().focus().deleteTable().run()}
                >
                  <Minus className="h-4 w-4 mr-2" />
                  Delete Table
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
      
      <ImageInsertDialog
        open={imageDialogOpen}
        onOpenChange={setImageDialogOpen}
        onImageInserted={addImage}
      />

      <LinkInsertDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        initialUrl={linkInitials.url}
        initialNewTab={linkInitials.newTab}
        initialNofollow={linkInitials.nofollow}
        onInsert={insertOrUpdateLink}
        onRemove={editor?.isActive('link') ? removeLink : undefined}
      />

      <AffiliateCtaInsertDialog
        open={affiliateDialogOpen}
        onOpenChange={setAffiliateDialogOpen}
        onInsert={insertAffiliateCta}
      />
    </>
  );
};
